import React, { useMemo, useState } from "react";
import { Button, Confirm, Icon } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import { P, motor, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { NumField } from "../common/NumField";
import { EditableTable } from "../common/EditableTable";
import { Notice, SaveBar, SaveErrors, Section, StatusBadge } from "../common/SectionCommon";
import { GraficoCatalogo } from "../common/Grafico";
import "../common/common.scss";
import "./EcmPortfolio.scss";

const tomar = (inst) => ({ ecms: inst.ecms || [], objetivos: inst.objetivos || [] });
const fmt = (v, d = 0) => P.fmt(v, d);
const ESTADO_MEDIDA = { identificada: "Brecha parcial", "en evaluación": "Mejorable", implementada: "Cumple" };

function Medida({ e, c, i, ops, seus, set, marcar, onBorrar, t }) {
  const sel = (clave, opciones, etiqueta) => (
    <div>
      <label htmlFor={`e${i}-${clave}`}>{etiqueta}</label>
      <select id={`e${i}-${clave}`} value={e[clave] == null ? "" : e[clave]} onChange={(ev) => set(i, clave, ev.target.value)}>
        {opciones.map(([v, txt]) => <option key={v} value={v}>{txt}</option>)}
      </select>
    </div>
  );
  const txt = (clave, etiqueta) => (
    <div>
      <label htmlFor={`e${i}-${clave}`}>{etiqueta}</label>
      <input id={`e${i}-${clave}`} type="text" value={e[clave] || ""} onChange={(ev) => set(i, clave, ev.target.value)} />
    </div>
  );
  const num = (clave, etiqueta) => (
    <div>
      <label>{etiqueta}</label>
      <NumField value={e[clave]} campo={clave} ariaLabel={`${etiqueta} — ${e.name}`} onChange={(v) => set(i, clave, v)} onError={(err) => marcar(`${i}.${clave}`, err ? 1 : 0)} />
    </div>
  );
  const manual = !e.baseOp;
  const suministro = P.esSuministro(e);
  const pendiente = String(c.payback || "").startsWith("Pendiente");
  return (
    <div className="ep-ecm__card">
      <div className="ep-ecm__head">
        <input type="text" className="ep-ecm__name" aria-label={t("energy_performance_ecm_name")} value={e.name || ""} onChange={(ev) => set(i, "name", ev.target.value)} />
        <StatusBadge estado={ESTADO_MEDIDA[e.status] || "Pendiente"}>{P.ecmEstadoTxt(e.status)}</StatusBadge>
        <Button type="button" icon basic size="mini" aria-label={t("energy_performance_delete_row")} onClick={() => onBorrar(i)}><Icon name="trash alternate outline" /></Button>
      </div>

      <div className="ep-kpis ep-ecm__result">
        <div className="ep-kpi"><div className="ep-kpi__label">{suministro ? t("energy_performance_ecm_generated") : t("energy_performance_ecm_savings_kwh")}</div><div className="ep-kpi__value">{fmt(suministro ? c.kwhRenovable : c.kwh)}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_ecm_savings_usd")}</div><div className="ep-kpi__value">{fmt(c.usd)}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_ecm_payback")}</div><div className={`ep-kpi__value ep-kpi__value--text ${pendiente ? "ep-ecm__pending" : ""}`}>{c.payback || "—"}</div></div>
      </div>
      {c._nota && <p className="ep-section__note">{c._nota}</p>}

      <div className="ep-grid">
        {sel("baseOp", [["", t("energy_performance_ecm_manual")], ...ops.map((o) => [o, o])], t("energy_performance_ecm_base_op"))}
        {sel("seu", [["", "—"], ...seus.map((s) => [s, s]), [t("energy_performance_general_services"), t("energy_performance_general_services")]], "USE")}
        {sel("fuenteE", [["", t("energy_performance_ecm_energy_from_op")], ["elec", t("energy_performance_ops_source_elec")], ["term", t("energy_performance_ops_source_term")]], t("energy_performance_ecm_energy"))}
        {sel("tipo", [["eficiencia", t("energy_performance_ecm_type_efficiency")], ["suministro", t("energy_performance_ecm_type_supply")]], t("energy_performance_ecm_type"))}
        {sel("status", [["identificada", "Identificada"], ["en evaluación", "En evaluación"], ["implementada", "Implementada"]], t("energy_performance_ecm_status"))}
        {num("inv", t("energy_performance_ecm_investment"))}
        {manual ? (
          suministro ? num("kwhRenovable", t("energy_performance_ecm_generated")) : (
            <>
              {num("kwh", t("energy_performance_ecm_savings_kwh"))}
              {num("usd", t("energy_performance_ecm_savings_usd"))}
            </>
          )
        ) : (
          <>
            {num("pct", t("energy_performance_ecm_pct"))}
            {num("frac", t("energy_performance_ecm_fraction"))}
            {txt("fracNota", t("energy_performance_ecm_fraction_note"))}
            {num("pctMin", t("energy_performance_ecm_pct_min"))}
            {num("pctMax", t("energy_performance_ecm_pct_max"))}
            {sel("pctMetodo", [["", t("energy_performance_ecm_method_none")], ["analogia", t("energy_performance_ecm_method_analogy")], ["fabricante", t("energy_performance_ecm_method_maker")], ["piloto", t("energy_performance_ecm_method_pilot")], ["auditoria", t("energy_performance_ecm_method_audit")]], t("energy_performance_ecm_pct_method"))}
            <div className="ep-grid__wide">{txt("pctFuente", t("energy_performance_ecm_pct_source"))}</div>
          </>
        )}
      </div>

      {!manual && !pendiente && c.calc && (
        <p className="ep-ecm__trace">
          <strong>{t("energy_performance_ecm_how")}:</strong> {e.baseOp} = {fmt(c.calc.baseTotal)} kWh/{t("energy_performance_year")}
          {c.calc.base !== c.calc.baseTotal && <> → {t("energy_performance_ecm_affected_base")} {fmt(c.calc.base)} kWh</>}
          {" × "}{fmt(e.pct, e.pct % 1 ? 1 : 0)}% = <strong>{fmt(suministro ? c.kwhRenovable : c.kwh)} kWh</strong>
          {!c.calc.sinTarifa && <> × {c.calc.tar} USD/kWh = <strong>{fmt(c.usd)} USD/{t("energy_performance_year")}</strong></>}
        </p>
      )}

      <h5 className="ep-ecm__sub">{t("energy_performance_ecm_action_plan")}</h5>
      <div className="ep-grid">
        {txt("resp", t("energy_performance_seu_responsible"))}
        {txt("fecha", t("energy_performance_ecm_date"))}
        {txt("recursos", t("energy_performance_ecm_resources"))}
        {txt("metodo", t("energy_performance_ecm_verification_method"))}
        {e.status === "implementada" && (
          <>
            {sel("verificacion", [["estimado", t("energy_performance_ecm_estimated")], ["verificado", t("energy_performance_ecm_verified")]], t("energy_performance_ecm_savings_status"))}
            {num("ahorro_verificado_kwh", t("energy_performance_ecm_verified_kwh"))}
          </>
        )}
      </div>
    </div>
  );
}

// Medidas de Conservación de Energía (ECM) y objetivos energéticos (6.2) — hoja 01 filas 8, 24, 47, 85–87, 110–111,
// 117, 122, 136, 145, 162.
export function EcmPortfolio({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { archivo, inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar, marcarInvalidos, invalidos, version } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  const [aBorrar, setABorrar] = useState(null);

  // recálculo en vivo con lo que se está editando (cascada, tarifas, repago)
  const vivo = useMemo(() => (archivo && b ? motor.calcularVista({ ...archivo, instalacion: { ...inst, ...b } }) : null), [archivo, inst, b]);
  const fv = (vivo && vivo.f) || f;

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;
  if (!fv) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>;

  const lista = b.ecms;
  const set = (i, clave, valor) => cambiar("ecms", lista.map((e, j) => (j === i ? { ...e, [clave]: valor } : e)));
  const calc = (i) => {
    const e = fv.ecms[i] || {};
    return { ...e, calc: e.baseOp ? P.ecmCalc(fv, e) : null };
  };
  const ops = (fv.operations || []).map((o) => o.nombre);
  const seus = (fv.seus || []).map((u) => u.name);
  const eficiencia = fv.ecms.filter((e) => !P.esSuministro(e));
  const totalKwh = eficiencia.reduce((a, e) => a + (Number(e.kwh) || 0), 0);
  const totalUsd = fv.ecms.reduce((a, e) => a + (Number(e.usd) || 0), 0);
  const renovable = fv.ecms.reduce((a, e) => a + (Number(e.kwhRenovable) || 0), 0);
  const implementadas = fv.ecms.filter((e) => e.status === "implementada").length;
  const errorVivo = dirty && vivo && vivo.error;

  return (
    <div className="ep-screen ep-ecm">
      <h3 className="ep-screen__title">{t("energy_performance_ecm_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_ecm_intro")}</p>
      {errorVivo && <Notice tipo="danger" titulo={t("energy_performance_live_invalid")}>{vivo.error}</Notice>}

      <div className="ep-kpis">
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_ecm_total_savings")}</div><div className="ep-kpi__value">{fmt(totalKwh)}</div><div className="ep-kpi__sub">kWh/{t("energy_performance_year")} · {eficiencia.length} {t("energy_performance_ecm_measures")}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_ecm_savings_usd")}</div><div className="ep-kpi__value">{fmt(totalUsd)}</div><div className="ep-kpi__sub">USD/{t("energy_performance_year")}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_ecm_renewable")}</div><div className="ep-kpi__value">{fmt(renovable)}</div><div className="ep-kpi__sub">kWh/{t("energy_performance_year")} · A.6.3</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_ecm_implemented")}</div><div className="ep-kpi__value">{implementadas}</div><div className="ep-kpi__sub">{t("energy_performance_of")} {fv.ecms.length}</div></div>
      </div>

      <div key={`ecms-${version}`}>
        {lista.map((e, i) => (
          <Medida key={e.id || i} e={e} c={calc(i)} i={i} ops={ops} seus={seus} set={set} marcar={marcarInvalidos} onBorrar={setABorrar} t={t} />
        ))}
        {!lista.length && <p className="ep-section__note">{t("energy_performance_ecm_empty")}</p>}
      </div>
      <Button type="button" basic size="small" onClick={() => cambiar("ecms", [...lista, { ...P.ROW_TEMPLATES.ecms(), payback: undefined }])}>
        <Icon name="plus" /> {t("energy_performance_ecm_add")}
      </Button>

      <div className="ep-charts"><GraficoCatalogo f={fv} id="ecm_ahorro" t={t} /><GraficoCatalogo f={fv} id="ecm_payback" t={t} /><GraficoCatalogo f={fv} id="ecm_estado" t={t} /><GraficoCatalogo f={fv} id="objetivos" t={t} /></div>
      <Section title={t("energy_performance_obj_title")} note={t("energy_performance_obj_note")}>
        <EditableTable
          key={`obj-${version}`}
          t={t}
          rows={b.objetivos}
          onChange={(v) => cambiar("objetivos", v)}
          onErrors={(n) => marcarInvalidos("objetivos", n)}
          newRow={() => P.ROW_TEMPLATES.objetivos()}
          addLabel={t("energy_performance_obj_add")}
          emptyText={t("energy_performance_obj_empty")}
          columns={[
            { key: "objetivo", label: t("energy_performance_obj_objective") },
            { key: "meta_valor", label: t("energy_performance_obj_target"), type: "num" },
            { key: "meta_unidad", label: t("energy_performance_unit") },
            { key: "iden", label: t("energy_performance_obj_enpi"), type: "select", options: [["", "—"], ...Object.keys(P.IDEN_LIB).map((k) => [k, P.IDEN_LIB[k].nombre(fv)])] },
            { key: "fecha_limite", label: t("energy_performance_obj_deadline") },
            { key: "resp", label: t("energy_performance_seu_responsible") },
            { key: "ecms", label: t("energy_performance_obj_measures") },
            {
              key: "_avance", label: t("energy_performance_obj_progress"), type: "view", render: (o) => {
                const a = P.avanceObjetivo(fv, o);
                return (
                  <>
                    {a.ideVal != null && isFinite(a.ideVal) ? <div>{t("energy_performance_obj_current")}: <strong>{fmt(a.ideVal, 3)}</strong> {a.ideUni}{" "}
                      {a.cumple === true && <StatusBadge estado="Cumple">{t("energy_performance_obj_reached")}</StatusBadge>}
                      {a.cumple === false && <StatusBadge estado="Mejorable">{t("energy_performance_obj_in_progress")}</StatusBadge>}</div>
                      : <div>{t("energy_performance_obj_no_enpi")}</div>}
                    <div>{a.ecms.length ? t("energy_performance_obj_implemented").replace("{a}", a.impl).replace("{b}", a.ecms.length) : t("energy_performance_obj_no_measures")}</div>
                  </>
                );
              },
            },
          ]}
        />
      </Section>

      <Section title={t("energy_performance_ecm_protocol_title")} note={t("energy_performance_ecm_protocol_note")} />

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={invalidos} saving={saving} onSave={() => guardar({ ecms: lista, objetivos: b.objetivos })} onDiscard={descartar} t={t} />

      <Confirm
        open={aBorrar != null}
        content={t("energy_performance_confirm_delete_row")}
        cancelButton={t("cancel")}
        confirmButton={t("energy_performance_delete")}
        onCancel={() => setABorrar(null)}
        onConfirm={() => { cambiar("ecms", lista.filter((_, j) => j !== aBorrar)); setABorrar(null); }}
      />
    </div>
  );
}
